import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@ff-os/shared-react/components/buttons/button/Button";
import { Icon } from "@ff-os/shared-react/components/icons/Icon";
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { selectActiveVariant } from '../../../_redux-groups';
import { selectFrameId, setFrameId, selectAccessoryId, setAccessoryId, setisFotofunPanelOpen } from "../../../_redux-ui";
import { applyAccessoryThunk } from '../../../thunks/applyAccessoryThunk';
import { applyMagicBorderThunk } from "../../../thunks/applyMagicBorderThunk";
import { updateVariant } from "../../../thunks/updateVariant";
import { FotoFunMobileApplyButton } from "../../fotofun/components/FotoFunMobileApplyButton";
import styles from "./PhotoPrintsGroupWindowNavigation.module.scss";

export const PhotoPrintsGroupWindowNavigation = ({
    photoIds,
    browse,
    close,
    fotofun,
    groupId,
    hasApplied,
    isPhotoFunProcessing
}: Props) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const frameIdMobile = useSelector(selectFrameId);
    const accessoryIdMobile = useSelector(selectAccessoryId);
    const activeVariant = useSelector((state: PhotoPrintsPdpState) => selectActiveVariant(state, groupId));
    const hasWhiteBorderActive = activeVariant.borderId && activeVariant.borderId === 'with-border';

    const [hasAppliedLocal, setHasAppliedLocal] = useState(hasApplied ?? false);

    useEffect(() => {
        if (isPhotoFunProcessing) {
            setHasAppliedLocal(true);
        }
    }, [isPhotoFunProcessing]);

    const removeFrames = () => {
        if (groupId) {
            dispatch(applyMagicBorderThunk(groupId, ""));
        }

        setHasAppliedLocal(true);
        dispatch(setFrameId(""));
        dispatch(setAccessoryId(""));
        close();
    };

    const handleApplyClick = () => {
        if (frameIdMobile && frameIdMobile !== "") {
            dispatch(applyMagicBorderThunk(groupId, frameIdMobile));
            dispatch(setisFotofunPanelOpen(false));
        }

        if (accessoryIdMobile && accessoryIdMobile !== "") {
            dispatch(applyAccessoryThunk({
                groupId: groupId,
                accessoryId: accessoryIdMobile,
                borderId: activeVariant.borderId}));
            dispatch(setisFotofunPanelOpen(false));
        }

        if (hasWhiteBorderActive) {
            dispatch(updateVariant(activeVariant.paperId, activeVariant.sizeId, groupId, "without-border"));
        }

        setHasAppliedLocal(true);
        dispatch(setFrameId(""));
        dispatch(setAccessoryId(""));
    };

    if (fotofun) {
        return (
            <>
                {hasAppliedLocal ? (
                    <div className={styles.container}>
                        <button
                            onClick={removeFrames}
                            className={styles.FotofunPreviewButton}
                        >
                            <span className={styles.text}>{t("commons.cancel")}</span>
                        </button>
                        <Button onClick={() => {
                            dispatch(setisFotofunPanelOpen(false));
                            close();
                        }}>{t("commons.save")}</Button>
                    </div>
                ) : (
                    <div className={styles.container}>
                        <button onClick={close} className={styles.FotofunPreviewButton}>
                            <Icon
                                size="small"
                                type="general-ui-chevron-left"
                                strokeWidth={2}
                                className={styles.eyeIcon}
                            />
                            <span className={styles.text}>{t("commons.previous")}</span>
                        </button>
                        <FotoFunMobileApplyButton onClick={handleApplyClick} />
                    </div>
                )}
            </>
        );
    }

    return (
        <div className={styles.container}>
            {photoIds.length > 0 ? (
                <Button style="outline" onClick={browse}>
                    {t("photo_prints.add_more")}
                </Button>
            ) : (
                <Button style="outline" onClick={browse}>
                    {t("photo_prints.add")}
                </Button>
            )}

            <Button onClick={close}>{t("commons.save")}</Button>
        </div>
    );
};

type Props = {
    photoIds: string[];
    fotofun?: boolean;
    groupId: string;
    hasApplied?: boolean;
    isPhotoFunProcessing?: boolean;
    browse: () => void;
    close: () => void;
};